import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SocialAuthButtons from "../components/common/SocialAuthButtons";
import { Eye, EyeOff } from "lucide-react";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [formData, setFormData] = useState({
    email: localStorage.getItem("briq_remembered_email") || "",
    password: ""
  });
  const [rememberMe, setRememberMe] = useState(() => {
    return localStorage.getItem("briq_remember_me") === "true";
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Enforce pure light mode for login screen
  useEffect(() => {
    document.documentElement.classList.remove('dark');
  }, []);

  // Automatically detect incoming token from landing page redirect or clear old prototype tokens
  useEffect(() => {
    // Support seamless single sign-on from landing page (?token=...&user=...)
    const params = new URLSearchParams(window.location.search);
    const incomingToken = params.get('token');
    const incomingUser = params.get('user');
    if (incomingToken && !incomingToken.startsWith('prototype-jwt-token')) {
      localStorage.setItem('token', incomingToken);
      let parsedUser = null;
      if (incomingUser) {
        try {
          parsedUser = JSON.parse(incomingUser);
        } catch {
          try {
            parsedUser = JSON.parse(decodeURIComponent(incomingUser));
          } catch (e) {
            console.warn("Could not parse user param:", e);
          }
        }
      }
      // Fallback: extract user data directly from the JWT payload
      if (!parsedUser && incomingToken) {
        try {
          const base64Url = incomingToken.split('.')[1];
          if (base64Url) {
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
              atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
            );
            parsedUser = JSON.parse(jsonPayload);
          }
        } catch (e) {
          console.warn("Could not extract user from token:", e);
        }
      }
      if (parsedUser) {
        localStorage.setItem('user', JSON.stringify(parsedUser));
      }
      localStorage.setItem('briq_last_login', new Date().toISOString());
      const destination = params.get('redirect') || (parsedUser?.role === 'Customer' ? '/admin/customer-portal' : '/admin/dashboard');
      window.location.replace(destination);
      return;
    }

    const token = localStorage.getItem('token');
    if (token && token.startsWith('prototype-jwt-token')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // 🟢 Call the unified login function with rememberMe
      const result = await login(formData.email, formData.password, rememberMe);

      if (result.success) {
        localStorage.setItem('briq_last_login', new Date().toISOString());
        toast.success("Login Successful!");
        setTimeout(() => {
          // homeRoute is set by AuthContext based on user's permissions:
          // Admin/Super Admin → /admin/dashboard
          // Worker (self-service) → /admin/workforce/my-payslips
          navigate(result.homeRoute || "/admin/dashboard");
        }, 500);
      } else {
        toast.error(result.message || "Invalid credentials or User not found.");
      }
    } catch (err) {
      console.error("Login Error:", err);
      toast.error("An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-auth-page="true" className="min-h-screen flex items-center justify-center bg-[#F7F4EE] px-4 auth-light-only">
      <div className="bg-white shadow-xl rounded-2xl p-8 w-full max-w-md border border-[#E1E7EE]">
        <div className="text-center mb-6">
          <img
            src="/assets/branding/briqerp-logo-light.svg"
            alt="BriqERP"
            className="h-10 sm:h-12 w-auto mx-auto object-contain mb-2"
          />
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
          {/* Decoy fields to catch browser password manager autofill */}
          <div style={{ position: "absolute", top: "-9999px", left: "-9999px", opacity: 0, height: 0, width: 0, overflow: "hidden" }} aria-hidden="true" tabIndex={-1}>
            <input type="text" name="decoy_admin_username" tabIndex={-1} autoComplete="off" defaultValue="" />
            <input type="password" name="decoy_admin_password" tabIndex={-1} autoComplete="new-password" defaultValue="" />
          </div>

          <div>
            <label className="block text-xs font-bold text-[#1B2A41] uppercase tracking-wide mb-1">
              Email Address
            </label>
            <input
              type="email"
              name="email"
              autoComplete="off"
              required
              value={formData.email}
              onChange={handleChange}
              className="w-full border border-gray-300 px-4 py-2.5 rounded-lg text-sm text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#E8612C] focus:border-transparent transition"
              placeholder="admin@example.com"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-[#1B2A41] uppercase tracking-wide mb-1">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                autoComplete="new-password"
                required
                value={formData.password}
                onChange={handleChange}
                className="w-full border border-gray-300 px-4 py-2.5 pr-10 rounded-lg text-sm text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#E8612C] focus:border-transparent transition"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Remember Me & Forgot Password Row */}
          <div className="flex items-center justify-between pt-1">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                name="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded text-[#E8612C] focus:ring-[#E8612C] border-gray-300 accent-[#E8612C] cursor-pointer"
              />
              <span className="text-xs font-medium text-[#475569]">Remember me</span>
            </label>

            <Link
              to="/forgot-password"
              className="text-xs font-semibold text-[#E8612C] hover:text-[#D44E1C] hover:underline transition"
            >
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full bg-[#E8612C] text-white py-2.5 rounded-lg hover:bg-[#D44E1C] transition-all font-semibold shadow-md disabled:opacity-50 cursor-pointer ${loading ? 'cursor-not-allowed' : ''}`}
          >
            {loading ? "Authenticating..." : "Sign In"}
          </button>

          <p className="text-xs text-center text-[#6B7A90] mt-3">
            Need an account?{" "}
            <Link to="/signup" className="text-[#E8612C] font-semibold hover:underline">
              Sign Up
            </Link>
          </p>

          {/* Divider */}
          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-[11px] uppercase tracking-wider">
              <span className="bg-white px-2.5 text-[#6B7A90] font-medium">Or continue with</span>
            </div>
          </div>

          {/* Google & Apple Social Login */}
          <SocialAuthButtons role="Super Admin" mode="login" />
        </form>


      </div>
    </div>
  );
}